/**
 * A ```plot fence, on its way to becoming a figure.
 *
 * Blocks arrive character by character while the tutor writes, so the failure
 * path is the common path for a second or two. An incomplete spec shows a
 * drawing placeholder; only a *finished* block that still doesn't validate is
 * reported as an error.
 */

import { useMemo } from "react";
import { Loader2 } from "lucide-react";
import { parsePlotSpec } from "../../shared/plot-spec.ts";
import { Plot } from "../plot/Plot.tsx";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert.tsx";

interface PlotBlockProps {
  readonly source: string;
  readonly index?: number;
  /** False while the answer is still streaming in. */
  readonly complete: boolean;
}

export function PlotBlock({ source, index, complete }: PlotBlockProps) {
  const result = useMemo(() => parsePlotSpec(source), [source]);

  if (result.ok) {
    return <Plot spec={result.spec} index={index} />;
  }

  if (!complete) {
    return (
      <div
        role="status"
        data-plot="pending"
        className="my-6 flex items-center justify-center gap-2 rounded-lg border border-dashed border-border-strong bg-muted p-6 font-sans text-sm text-muted-foreground"
      >
        <Loader2 className="animate-spin motion-reduce:animate-none" />
        drawing figure…
      </div>
    );
  }

  return (
    <Alert
      role="alert"
      data-plot="error"
      className="my-4 rounded-l-none border-0 border-l-[3px] border-l-warning bg-muted font-sans text-sm"
    >
      <AlertTitle className="font-semibold">
        This figure couldn't be drawn.
      </AlertTitle>
      <AlertDescription className="text-secondary-foreground">
        <p>{result.error}</p>
        <details className="w-full">
          <summary className="cursor-pointer text-muted-foreground">
            Show the raw block
          </summary>
          <pre className="mt-1 overflow-x-auto font-mono text-xs">{source}</pre>
        </details>
      </AlertDescription>
    </Alert>
  );
}
